import React from 'react';
import { CloudSunIcon, CloudRainIcon, SunIcon, CloudIcon, CloudSnowIcon, WindIcon, DropletIcon } from 'lucide-react';
export function WeatherSelector({
  selectedCity,
  setSelectedCity,
  weatherData
}) {
  const cities = [
  // North America
  'New York', 'Los Angeles', 'Chicago', 'Miami',
  // Canada
  'Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa',
  // Europe
  'London', 'Paris', 'Berlin', 'Rome', 'Barcelona',
  // Asia
  'Tokyo', 'Seoul', 'Singapore', 'Dubai', 'Hong Kong',
  // Oceania
  'Sydney', 'Melbourne', 'Auckland'];
  // Define regions that use Celsius (everyone except US)
  const celsiusRegions = ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa', 'London', 'Paris', 'Berlin', 'Rome', 'Barcelona', 'Tokyo', 'Seoul', 'Singapore', 'Dubai', 'Hong Kong', 'Sydney', 'Melbourne', 'Auckland' // Oceania
  ];
  // Check if city uses Celsius
  const usesCelsius = celsiusRegions.includes(selectedCity);
  // Convert Fahrenheit to Celsius
  const toCelsius = fahrenheit => {
    return Math.round((fahrenheit - 32) * 5 / 9);
  };
  // Display temperature in the appropriate unit
  const displayTemperature = () => {
    if (usesCelsius) {
      return `${toCelsius(weatherData.temperature)}°C`;
    } else {
      return `${weatherData.temperature}°F`;
    }
  };
  // Additional weather details (simulated from weather.net)
  const getAdditionalWeatherDetails = () => {
    // Simulated data based on condition and temperature
    const humidity = Math.round(weatherData.condition.toLowerCase().includes('rain') ? 75 + Math.random() * 15 : 40 + Math.random() * 30);
    const windSpeed = usesCelsius ? Math.round(5 + Math.random() * 20) :
    // km/h for Celsius regions
    Math.round(3 + Math.random() * 12); // mph for Fahrenheit regions
    const windUnit = usesCelsius ? 'km/h' : 'mph';
    const feelsLike = usesCelsius ? toCelsius(weatherData.temperature) + (Math.random() > 0.5 ? -2 : 2) : weatherData.temperature + (Math.random() > 0.5 ? -3 : 3);
    return {
      humidity,
      windSpeed,
      windUnit,
      feelsLike
    };
  };
  const additionalDetails = getAdditionalWeatherDetails();
  const getWeatherIcon = condition => {
    switch (condition.toLowerCase()) {
      case 'rainy':
        return <CloudRainIcon className="h-10 w-10 sm:h-12 sm:w-12 text-blue-500" data-id="element-255" />;
      case 'sunny':
      case 'clear':
        return <SunIcon className="h-10 w-10 sm:h-12 sm:w-12 text-yellow-500" data-id="element-256" />;
      case 'cloudy':
      case 'partly cloudy':
        return <CloudIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" data-id="element-257" />;
      case 'snowy':
        return <CloudSnowIcon className="h-10 w-10 sm:h-12 sm:w-12 text-blue-200" data-id="element-258" />;
      default:
        return <CloudSunIcon className="h-10 w-10 sm:h-12 sm:w-12 text-blue-400" data-id="element-259" />;
    }
  };
  return <div id="weather" className="bg-white rounded-xl shadow-md p-4 h-full" data-id="element-260">
      <h2 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-261">
        Weather Location
      </h2>
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-262">
        <div className="w-full sm:w-1/2" data-id="element-263">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1" data-id="element-264">
            Select City
          </label>
          <select value={selectedCity} onChange={e => setSelectedCity(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm" data-id="element-265">
            {cities.map(city => <option key={city} value={city} data-id="element-266">
                {city}
              </option>)}
          </select>
        </div>
        <div className="w-full sm:w-1/2" data-id="element-267">
          <div className="flex items-center justify-between h-full" data-id="element-268">
            <div data-id="element-269">
              <h3 className="text-base sm:text-lg font-medium text-gray-800" data-id="element-270">
                {selectedCity}
              </h3>
              <div className="flex items-center mt-1" data-id="element-271">
                <span className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-272">
                  {displayTemperature()}
                </span>
                <span className="ml-2 text-xs sm:text-sm text-gray-600" data-id="element-273">
                  • {weatherData.condition}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1" data-id="element-274">
                Season: {weatherData.season}
              </p>
              <div className="mt-2 flex flex-col space-y-1" data-id="element-275">
                <div className="flex items-center text-xs text-gray-600" data-id="element-276">
                  <DropletIcon className="h-3 w-3 mr-1 text-blue-500" data-id="element-277" />
                  <span data-id="element-278">Humidity: {additionalDetails.humidity}%</span>
                </div>
                <div className="flex items-center text-xs text-gray-600" data-id="element-279">
                  <WindIcon className="h-3 w-3 mr-1 text-blue-400" data-id="element-280" />
                  <span data-id="element-281">
                    Wind: {additionalDetails.windSpeed}{' '}
                    {additionalDetails.windUnit}
                  </span>
                </div>
                <div className="flex items-center text-xs text-gray-600" data-id="element-282">
                  <div className="h-3 w-3 mr-1" data-id="element-283" />
                  <span data-id="element-284">
                    Feels like:{' '}
                    {usesCelsius ? `${Math.round(additionalDetails.feelsLike)}°C` : `${Math.round(additionalDetails.feelsLike)}°F`}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0" data-id="element-285">
              {getWeatherIcon(weatherData.condition)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}